import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
} from "@mui/material";
import { Drafts, AdsClick, Reply, CampaignOutlined, PublishedWithChangesOutlined, MarkEmailReadOutlined, FileDownloadOutlined, } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { 
  getCampaignAnalytics,
  getEmailsByCampaign, 
} from "../api/apiService";
import { BarChart } from "@mui/x-charts";
import { PieChart } from "@mui/x-charts";
import * as XLSX from "xlsx";

export default function Analytics() {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const clientId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('clientId')) ?? '';
    } catch {
      return '';
    }
  }, []);

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsSummary, setCampaignsSummary] = useState([]);

  const [selectedCampaign, setSelectedCampaign] = useState("all");

  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [selectedCampaignName, setSelectedCampaignName] = useState("");

  const [emailLoading, setEmailLoading] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const res = await getCampaignAnalytics(clientId);
      const normalizedRows = res.data.campaigns.map((res) => ({
        ...res,
        // startDate: new Date(res.startDate?.$date || res.startDate).toLocaleDateString(),
        // startTime: new Date(res.startTime?.$date || res.startTime).toLocaleTimeString(),
      }));
      const campSummary = res.data.summary;
      setLoading(false);
      setRows(normalizedRows);
      setRowCount(res.data.total);
      setCampaigns(normalizedRows);
      setCampaignsSummary(campSummary || { totalCampaigns:0, activeCampaigns:0, totalEmailsSent:0, averageOpenRate:0, averageClickRate:0, averageBounceRate:0 });
    } catch (error) {
      setLoading(false);
      setSnackbar({ open: true, message: 'Not able to fetch campaign details', severity: 'error' });
    }
  }, [clientId]); 

  // On Campaign name click
  const handleCampaignClick = async (campaignId, campaignName) => {
    try {
      setSelectedCampaignName(campaignName);
      setOpenDialog(true);
      setEmailLoading(true);
      const res = await getEmailsByCampaign(campaignId);
      setEmailLogs(res.data || []);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to load email logs", severity: "error" });
    } finally {
      setEmailLoading(false);
    }
  };

  //Export to Excel 
  const exportToExcel = () => {
    if (!emailLogs || emailLogs.length === 0) {
      setSnackbar({ open: true, message: 'No records to export', severity: 'warning' });
      return;
    }

    // Filter and transform the data
    const filteredData = emailLogs.map((row) => ({
      Lead: row.to,
      Status: row.status,
      Subject: row.subject,
      SentAt: row.sentAt ? new Date(row.sentAt).toLocaleString() : "",
    }));

    // Convert JSON data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(filteredData);

    // Create a workbook and append worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "EmailLogs");

    // Directly trigger file download
    XLSX.writeFile(workbook, `EmailLogs_${selectedCampaignName || "campaign"}.xlsx`);
  };

  // Calculate aggregate metrics
  const aggregatedMetrics = useMemo(() => {

    if (!campaignsSummary) return { openRate: 0, clickRate: 0, bounceRate: 0 };

    const openRate = (campaignsSummary.averageOpenRate ?? 0).toFixed(1);  // 1 decimal place // 0 whole number
    const clickRate = (campaignsSummary.averageClickRate ?? 0).toFixed(1);
    const bounceRate = (campaignsSummary.averageBounceRate ?? 0).toFixed(1);

    return { openRate, clickRate, bounceRate };
  }, [campaignsSummary]);

  // Memoize PieChart series
  const engagementSeries = useMemo(() => [{
    innerRadius: 60,
    outerRadius: 100,
    paddingAngle: 2,
    cornerRadius: 3,
    data: [
      { id: 0, value: campaignsSummary.averageOpenRate ?? 0, label: 'Open', color: '#22c55e' },
      { id: 1, value: campaignsSummary.averageClickRate ?? 0, label: 'Click', color: '#6366f1' },
      { id: 2, value: campaignsSummary.averageBounceRate ?? 0, label: 'Bounce', color: '#f59e0b' },
    ]
  }], [campaignsSummary]);

  // Memoize BarChart series and xAxis
  const keyVolumesSeries = useMemo(() => [{
    data: [
      campaignsSummary.totalCampaigns || 0,
      campaignsSummary.activeCampaigns || 0,
      campaignsSummary.totalEmailsSent || 0
    ],
    label: 'Count'
  }], [campaignsSummary]);
  
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);


  // Filter campaigns for the graph based on selected value
  const filteredCampaigns = selectedCampaign === "all"
    ? campaigns
    : campaigns.filter(c => c.id === selectedCampaign);

  const xLabels = filteredCampaigns.map((c) => c.name);
  const openData = filteredCampaigns.map((c) => c.openRate);
  const clickData = filteredCampaigns.map((c) => c.clickRate);
  const bounceData = filteredCampaigns.map((c) => c.bounceRate);

  const performanceXAxis = useMemo(() => [{ data: xLabels, label: 'Campaign Name' }], [xLabels]);
  const performanceSeries = useMemo(() => [
    { data: openData, label: "OpenRate" },
    { data: clickData, label: "ClickRate" },
    { data: bounceData, label: "BounceRate" },
  ], [openData, clickData, bounceData]);

  const columns = [
    // { field: "name", headerName: "Campaign Name", width: 200, flex: 1 },
    {
      field: "name",
      headerName: "Campaign Name",
      width: 580,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center", 
            height: "100%",
          }}
        >
          <Typography
            onClick={() => handleCampaignClick(params.row.id, params.value)}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline"
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { field: "status", headerName: "Status", width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "active" ? "success" : params.value === "completed" ? "primary" : "default"}
          variant="filled"
          size="small"
          sx={{my:1}}
        />
      ),
    },
    { field: "emailsSent", headerName: "Sent", type: "number", width: 110, },
    { field: "emailsOpened", headerName: "Opens", type: "number", width: 110 },
    { field: "emailsClicked", headerName: "Clicks", type: "number", width: 110 },
    { field: "bounces", headerName: "Bounces", type: "number", width: 110 },
    { field: "unsubscribes", headerName: "Unsubscribes", type: "number", width: 110 },
  ];

  const emailColumns = [
    { field: "to", headerName: "Email", width: 250 },
    { field: "status", headerName: "Status", width: 100 },
    { field: "subject", headerName: "Subject", flex: 1 },
    {
      field: "sentAt",
      headerName: "Sent At",
      width: 200,
      renderCell: (params) => {
        if (!params.row.sentAt) return "—";
        return new Date(params.row.sentAt).toLocaleString("en-US", {
          dateStyle: "medium",
          timeStyle: "short",
        });
      },
    },
  ];

  return (
    <Box sx={{
      minHeight: '100%',
      py: 2,
      background: (theme) => theme.palette.gradient.surface
    }}>

      {/* Error display via Alert and snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Typography variant="h5" fontWeight={600}>Reports and Analytics</Typography>
      {/* Cards 01*/}
      <Grid container spacing={2} alignItems="center" sx={{ my: 2 }}>
        {[
          { title: "Total Campaigns", value: campaignsSummary.totalCampaigns || '-', icon: <CampaignOutlined fontSize="large" color="primary" /> },
          { title: "Active Campaigns", value: campaignsSummary.activeCampaigns || '-', icon: <PublishedWithChangesOutlined fontSize="large"  color="primary" /> },
          { title: "Total Emails Sent", value: campaignsSummary.totalEmailsSent || '-', icon: <MarkEmailReadOutlined fontSize="large"  color="primary" /> },
        ].map((item, i) => (
          <Grid key={i} size={{xs:12, sm:4, md:4}}>
            <Card elevation={0} sx={{ borderRadius: 3, px: 3, border: '1px solid', borderColor: 'divider', transition: 'transform 200ms ease, box-shadow 200ms ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {item.icon}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>{item.title}</Typography>
                  </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>{item.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Cards 02*/}
      <Grid container spacing={2} alignItems="center" sx={{ my: 2 }}>
        {[
          { title: "Open Rate", value: aggregatedMetrics.openRate !== 0 ? aggregatedMetrics.openRate + '%' : '-', icon: <Drafts fontSize="large" color="primary" /> },
          { title: "Click Rate", value: aggregatedMetrics.clickRate !== 0 ? aggregatedMetrics.clickRate + '%' : "-", icon: <AdsClick fontSize="large"  color="primary" /> },
          { title: "Bounce Rate", value: aggregatedMetrics.bounceRate !== 0 ? aggregatedMetrics.bounceRate + '%' : "-", icon: <Reply fontSize="large"  color="primary" /> },
        ].map((item, i) => (
          <Grid key={i} size={{xs:12, sm:4, md:4}}>
            <Card elevation={0} sx={{ borderRadius: 3, px: 3, border: '1px solid', borderColor: 'divider', transition: 'transform 200ms ease, box-shadow 200ms ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {item.icon}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>{item.title}</Typography>
                  </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>{item.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Engagement distribution Pie */}
      <Grid container spacing={2} alignItems="stretch" sx={{ my: 1 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 1 }}>Engagement distribution</Typography>
              <PieChart
                series={engagementSeries}
                height={280}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 1 }}>Key volumes</Typography>
              <BarChart
                xAxis={[{ data: ['Campaigns', 'Active', 'Emails Sent'], label: 'Metric' }]}
                series={keyVolumesSeries}
                height={280}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bar Chart - Performance Metrics */}
      <Grid container spacing={1} sx={{ my: 2 }}>
        <Grid size={{xs:12}} >
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
              <Typography variant="body1" color="primary" fontWeight={700}>Performance Metrics</Typography>

               {/* Campaign Dropdown */}
              <FormControl size="small" sx={{ minWidth: { xs: 180, md: 250 } }}>
                <Select
                  sx={{borderRadius:2}}
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                >
                  <MenuItem value="all">All Campaigns</MenuItem>
                  {campaigns.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <CardContent>
              <BarChart
                xAxis={performanceXAxis}
                series={performanceSeries}
                height={300}
             />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaign Effectiveness Table */}
      <Grid container spacing={1} sx={{ my: 2 }}>
        <Grid  size={{xs:12}}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", px: 3, py: 2 }}>
              <Typography variant="body1" color="primary" fontWeight={700}>Campaign Effectiveness</Typography>
            </Box>
            <CardContent sx={{px: 3}}>

              <Box sx={{ height: 400, width: "100%" }}>
                <DataGrid
                sx={{height:400,borderRadius:2, p:1}}
                rows={rows} 
                columns={columns}
                loading={loading}
                getRowId={(row) => row.id}
                rowCount={rowCount}
                paginationMode="server"
                hideFooterPagination
                //getRowHeight={() => "auto"}
                disableRowSelectionOnClick
                />
              </Box>

            </CardContent>
          </Card>
        </Grid>
      </Grid>

     {/* Email Logs Dialog */}
      <Dialog open={openDialog} onClose={() => {setOpenDialog(false); setEmailLogs([]);}} fullWidth maxWidth="md" 
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Email Logs -  {selectedCampaignName || 'campaign'}</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlined />}
              sx={{ borderRadius: 2, textTransform: 'none' }}
              onClick={exportToExcel}
            >
              Export
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: 400 }}>
            <DataGrid
              sx={{borderRadius:2}}
              rows={emailLogs}
              columns={emailColumns}
              getRowId={(row) => row.id}
              loading={emailLoading}
              disableRowSelectionOnClick
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
